import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  SearchOutlined,
  PlusOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import KpiCard from "./KpiCard";
import styles from "./kpiView.module.scss";
import { resolveKpiPivots, resolveKpiTitle } from "./kpiViewUtils";

function KpiCardSkeleton({ index }) {
  const { t } = useTranslation();
  return (
    <div
      className={styles.cardSkeleton}
      aria-label={t("team_kpi_view.kpi_loading", {
        index: index + 1,
        defaultValue: "Loading KPI {{index}}",
      })}
    >
      <div className={`${styles.skeletonLine} ${styles.skeletonLineTitle}`} />
      <div className={styles.skeletonMetrics}>
        <div className={styles.skeletonMetricBlock} />
        <div className={styles.skeletonMetricBlock} />
      </div>
      <div className={`${styles.skeletonLine} ${styles.skeletonLineChart}`} />
      <div className={`${styles.skeletonLine} ${styles.skeletonLineFooter}`} />
    </div>
  );
}

function kpiMatchesSearch(item, query) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;

  const title = resolveKpiTitle(item).toLowerCase();
  if (title.includes(q)) return true;

  const metrics = Array.isArray(item?.metric)
    ? item.metric
    : item?.metric != null
    ? [item.metric]
    : [];
  if (metrics.some((metric) => String(metric ?? "").toLowerCase().includes(q))) {
    return true;
  }

  return resolveKpiPivots(item).some((pivot) =>
    `${pivot.key} ${pivot.value}`.toLowerCase().includes(q)
  );
}

export default function KpiGroupSection({
  group,
  theme,
  loading = false,
  readOnly = false,
  reviewsPresentationMode = false,
  isFirstGroup = false,
  onRenameGroup,
  onDeleteGroup,
  onCardMenuAction,
  showMoveAction = false,
  onOpenReviews,
  onReviewsScrolled,
  onToggleAlert,
  onConfigureAlert,
  onEmptySlotClick,
  compareWindow = "30D",
  teamId,
}) {
  const { t } = useTranslation();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name || "");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  const filteredKpis = useMemo(() => {
    const list = (group.kpis || []).filter((kpi) =>
      kpiMatchesSearch(kpi, searchQuery)
    );
    return list;
  }, [group.kpis, searchQuery]);

  const showInitialSkeleton =
    loading && !searchQuery.trim() && (group.kpis || []).length === 0;

  const showNamedEmptyState =
    !loading &&
    !searchQuery.trim() &&
    Boolean(group.name) &&
    (group.kpis || []).length === 0;

  const slots = useMemo(() => {
    if (showInitialSkeleton) {
      return Array.from({ length: 4 }, () => "skeleton");
    }
    if (showNamedEmptyState) {
      return [];
    }
    const next = [...filteredKpis];
    if (!searchQuery.trim()) {
      while (next.length < 4) next.push(null);
    }
    return next;
  }, [filteredKpis, searchQuery, showInitialSkeleton, showNamedEmptyState]);

  useEffect(() => {
    setNameDraft(group.name || "");
  }, [group.name]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [searchOpen]);

  const toggleSearch = () => {
    setSearchOpen((prev) => {
      if (prev) setSearchQuery("");
      return !prev;
    });
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  const commitRename = () => {
    setEditingName(false);
    if (onRenameGroup) onRenameGroup(group, nameDraft.trim());
  };

  const handleDeleteGroup = () => {
    Modal.confirm({
      title: t("team_kpi_view.105", "Delete group"),
      content: t(
        "team_kpi_view.106",
        "Are you sure you want to remove this group name?",
      ),
      okText: t("button.2", "Delete"),
      cancelText: t("button.cancel", "Cancel"),
      centered: true,
      onOk: () => {
        if (onDeleteGroup) onDeleteGroup(group);
      },
    });
  };

  const renderGroupTitle = () => {
    if (readOnly) {
      if (group.name) {
        return <h3 className={styles.groupTitle}>{group.name}</h3>;
      }
      if (isFirstGroup) {
        return (
          <span className={styles.groupSectionLabel}>
            {t("team_kpi_view.101", "General")}
          </span>
        );
      }
      return null;
    }

    if (editingName) {
      return (
        <input
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => e.key === "Enter" && commitRename()}
          autoFocus
          className={styles.groupRenameInput}
        />
      );
    }

    if (group.name) {
      return (
        <>
          <h3 className={styles.groupTitle}>{group.name}</h3>
          <button
            type="button"
            className={styles.groupEditBtn}
            onClick={() => {
              setNameDraft(group.name);
              setEditingName(true);
            }}
            aria-label={t("team_kpi_view.104", "Rename")}
          >
            <EditOutlined />
          </button>
          <button
            type="button"
            className={styles.groupDeleteBtn}
            onClick={handleDeleteGroup}
            aria-label={t("team_kpi_view.105", "Delete group")}
          >
            <DeleteOutlined />
          </button>
        </>
      );
    }

    if (isFirstGroup) {
      return (
        <span className={styles.groupSectionLabel}>
          {t("team_kpi_view.101", "General")}
        </span>
      );
    }

    return (
      <button
        type="button"
        className={styles.groupTitlePlaceholder}
        onClick={() => setEditingName(true)}
      >
        + {t("team_kpi_view.4", "Add group")}
      </button>
    );
  };

  return (
    <section className={styles.group}>
      <div className={styles.groupHeader}>
        <div className={styles.groupTitleRow}>{renderGroupTitle()}</div>
        {!readOnly && (
          <div className={styles.groupHeaderActions}>
            {searchOpen && (
              <div className={styles.groupSearchField}>
                <SearchOutlined className={styles.groupSearchFieldIcon} />
                <input
                  ref={searchInputRef}
                  type="search"
                  className={styles.groupSearchInput}
                  placeholder={t("team_kpi_view.search_placeholder", "Search KPIs...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") closeSearch();
                  }}
                  aria-label={t("team_kpi_view.search_aria", "Search KPIs")}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.groupSearchClear}
                    onClick={() => setSearchQuery("")}
                    aria-label={t("team_kpi_view.search_clear", "Clear search")}
                  >
                    <CloseOutlined />
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              className={`${styles.groupSearchBtn} ${
                searchOpen ? styles.groupSearchBtnActive : ""
              }`}
              aria-label={
                searchOpen
                  ? t("team_kpi_view.search_close", "Close search")
                  : t("team_kpi_view.search_aria", "Search KPIs")
              }
              aria-pressed={searchOpen}
              onClick={toggleSearch}
            >
              <SearchOutlined />
            </button>
          </div>
        )}
      </div>

      {showNamedEmptyState ? (
        <div className={styles.groupEmpty}>
          <p className={styles.groupEmptyText}>
            {t("team_kpi_view.103", "No KPIs in this group yet")}
          </p>
          <p className={styles.groupEmptyHint}>
            {t(
              "team_kpi_view.107",
              "Create KPIs from the left panel to add them to this group.",
            )}
          </p>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {searchQuery.trim() && filteredKpis.length === 0 ? (
            <div className={styles.groupSearchEmpty}>
              {t("team_kpi_view.search_empty", "No matching KPIs")}
            </div>
          ) : null}
          {slots.map((kpi, index) =>
            kpi === "skeleton" ? (
              <KpiCardSkeleton key={`skeleton-${group.id}-${index}`} index={index} />
            ) : kpi ? (
              <KpiCard
                key={kpi.id ?? `kpi-${group.id}-${index}`}
                item={kpi}
                theme={theme}
                readOnly={readOnly}
                reviewsPresentationMode={reviewsPresentationMode}
                showMoveAction={showMoveAction}
                onMenuAction={onCardMenuAction}
                onOpenReviews={onOpenReviews}
                onReviewsScrolled={onReviewsScrolled}
                onToggleAlert={onToggleAlert}
                onConfigureAlert={onConfigureAlert}
                compareWindow={compareWindow}
                teamId={teamId}
              />
            ) : (
              <button
                type="button"
                key={`empty-${group.id}-${index}`}
                className={styles.cardEmptySlot}
                onClick={() => {
                  if (onEmptySlotClick) {
                    onEmptySlotClick();
                  }
                }}
                aria-label={t("team_kpi_view.addMetricCta", "+ Add metric")}
              >
                <span className={styles.cardEmptySlotIconWrap}>
                  <LineChartOutlined className={styles.cardEmptySlotIcon} />
                  <PlusOutlined className={styles.cardEmptySlotPlus} />
                </span>
                <span className={styles.cardEmptySlotLabel}>
                  {t("team_kpi_view.addMetricCta", "+ Add metric")}
                </span>
              </button>
            )
          )}
        </div>
      )}
    </section>
  );
}
